<!-- src/components/dashboard/PerformanceContent.vue -->
<template>
    <div class="mt-4 h-screen">

        <div v-if="$route.meta.module === 'matrix' || $route.meta.module === 'laying-birds'"
            class="h-full flex flex-col">
            <div class="bg-white p-2 rounded border border-gray-300">
                <BatchDivisionCard :batchesDivision="getSelectedBatch?.batchDivisions" :gender="$route?.meta?.gender" />
            </div>
            <div class="col-span-12 mt-4 border rounded p-1 bg-white " v-if="getSelectedBatchDivision?.performanceData">
                <ChartComponent id="convertGraphDailyWeight" type="xy"
                    :graph-props="convertGraphDailyWeight.props || {}" />
            </div>
            <div class="bg-white flex mt-6 w-full border border-gray-300 rounded"
                v-if="getSelectedBatchDivision?.performanceData">
                <table class="table-auto  text-sm text-center  w-full">
                    <thead class="text-gray-700">
                        <tr>
                            <th class=" px-3 py-1 ">{{ $t('age') }}</th>
                            <th v-for="(item, idx) in getSelectedBatchDivision?.performanceData.slice(0, 14).reverse()"
                                :key="'day-' + idx" class="border-x px-3 py-1">
                                {{ item.age }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="division in sortedSelectedBatchDivisions" :key="'weight-row-' + division.id">
                            <td class="font-semibold px-3 py-1">{{ division.type }} {{ division.identification }} G.P.D.
                            </td>
                            <td v-for="(item, idx) in division?.performanceData.slice(0, 14).reverse()"
                                :key="'gpd-' + idx" class="border-x px-3 py-1"
                                :class="item.gain >= 0 ? 'bg-lime-100' : 'bg-red-100'" :style="{
                                    color: '#000000'
                                }">
                                {{ item.gain }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="getSelectedBatchDivision?.performanceData" class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div class="border border-gray-300 p-2 rounded bg-white">
                    <ChartComponent id="convertGraphWeeklyWeight" type="xy"
                        :graph-props="convertGraphWeeklyWeight.props || {}" />
                </div>
                <div v-if="histogramGraphic" class="border border-gray-300 p-2 rounded bg-white">
                    <ChartComponent id="histogramGraphic" type="xy" :graph-props="histogramGraphic.props || {}" />
                </div>
            </div>
        </div>
        <iframe v-else class="rounded h-full w-full" :src="grafanaDashboardUrl" frameborder="0">
        </iframe>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { differenceInSeconds } from 'date-fns';
import { parseISO } from 'date-fns';
import i18n from '@/plugins/i18n';

import BatchDivisionCard from '@/components/property/BatchDivisionCard.vue';
import ChartComponent from "@/components/dashboard/ChartHighcharts";

export default {
    name: 'PerformanceContent',
    components: {
        BatchDivisionCard,
        ChartComponent,
    },
    props: {
        loading: Boolean,
    },
    computed: {
        ...mapGetters('environment', [
            'getSelectedBatch',
            'getSelectedBatchDivision',
            'getSelectedBatchDivisions',
            'getDashboardsSettings',
        ]),
        convertGraphDailyWeight() {
            const batch = this.getSelectedBatch;
            const selected = this.getSelectedBatchDivisions;

            if (!selected || selected.length === 0 || !batch) return null;

            const idealWeights = batch?.idealWeight?.mixed || {};
            const allSeries = [];

            selected.forEach((division, index) => {
                const colorBase = ['#00c080', '#0070c0', '#c00000', '#7030a0'][index % 4];
                const data = [...(division.performanceData.slice(0, 14) || [])].reverse();

                allSeries.push({
                    name: `${this.$t('weight')} - ${division.type} ${division.identification}`,
                    color: colorBase,
                    type: 'line',
                    data: data.map(item => {
                        const isHighlight = item.age % 7 === 0;
                        return {
                            y: item.weight,
                            marker: isHighlight
                                ? {
                                    radius: 6,
                                    fillColor: '#eda800',
                                    lineWidth: 2,
                                    lineColor: '#eda800'
                                }
                                : { enabled: true },
                            custom: {
                                age_day: item.age,
                                daily_gain: item.gain,
                                week: item.week,
                                box: division.identification,
                                type: division.type
                            }
                        };
                    })
                });
            });

            const longestCategories = selected
                .map(d => d.performanceData.map(i => `${i.age}`))
                .reduce((a, b) => (b.length > a.length ? b : a), [])
                .sort((a, b) => a - b)
                .slice(-14)

            const idealSeries = {
                name: this.$t('idealWeightRef'),
                color: '#808080',
                type: 'line',
                dashStyle: 'ShortDash',
                data: [...longestCategories]
                    .map(Number)
                    .sort((a, b) => a - b)
                    .map(age => {
                        const weight = idealWeights[age] ?? null;
                        return {
                            y: weight,
                            custom: { age_day: age }
                        };
                    })
                    .filter(p => p.y !== null)
                    .slice(-14)
            };

            allSeries.push(idealSeries);

            return {
                props: {
                    legend: {
                        enabled: true,
                        layout: 'horizontal',
                        align: 'center',
                        verticalAlign: 'bottom'
                    },
                    credits: { enabled: false },
                    title: {
                        text: `${this.$t('dailyWeight')} (${selected.length} ${this.pluralize(selected[0].type, selected.length)})`
                    },
                    yAxis: {
                        title: { text: `${this.$t('weight')} (g)` }
                    },
                    tooltip: {
                        useHTML: true,
                        formatter: function () {
                            const { age_day, week, box, type } = this.point.custom || {};
                            const day = `${i18n.t('day')} ${age_day || '-'}`;
                            const tooltipWeek = week ? `${i18n.t('week')} ${week}` : '';
                            return `
                        <div style="padding: 4px 8px">
                        <strong>${tooltipWeek ? `${tooltipWeek} - ` : ''}${day}${box ? ` (${type} ${box})` : ''}</strong><br/>
                        ${this.series.name.includes('Ideal')
                                    ? `${i18n.t('idealWeight')}: ${this.y?.toFixed(2)}g`
                                    : `${i18n.t('dailyWeight')}: ${this.y?.toFixed(2)}g`
                                }
                        </div>
                    `;
                        }
                    },
                    xAxis: [
                        {
                            categories: longestCategories,
                            tickInterval: Math.floor(longestCategories.length / 10) || 1
                        }
                    ],
                    series: allSeries
                }
            };
        },

        convertGraphWeeklyWeight() {
            const batch = this.getSelectedBatch;
            const selected = this.getSelectedBatchDivisions;
            if (!selected || selected.length === 0) return null;

            const allSeries = [];

            selected.forEach((division, index) => {
                const colorBase = ['#00c080', '#0070c0', '#c00000', '#7030a0'][index % 4];

                const data = division.performanceData
                    .filter(item => item.age % 7 === 0);

                allSeries.push({
                    name: `${this.$t('weight')} - ${division.type} ${division.identification}`,
                    color: colorBase,
                    type: 'line',
                    data: data.map(item => ({
                        x: Math.floor(item.age / 7),
                        y: item.weight,
                        custom: {
                            age_week: item.age,
                            week: Math.floor(item.age / 7),
                            box: division.identification,
                        }
                    }))
                });


            });

            const idealWeights = batch?.idealWeight?.mixed || {};

            const longestCategories = selected
                .map(d => d.performanceData.filter(i => i.age % 7 === 0).map(i => `${i.age}`))
                .reduce((a, b) => (b.length > a.length ? b : a), []);

            const idealSeries = {
                name: this.$t('idealWeightRef'),
                color: '#808080',
                type: 'line',
                dashStyle: 'ShortDash',
                data: [...longestCategories]
                    .map(Number)
                    .sort((a, b) => a - b)
                    .map(age => {
                        const weight = idealWeights[age] ?? null;
                        return {
                            x: age / 7,
                            y: weight,
                            custom: {
                                age_day: age,
                                week: age / 7,
                            }
                        };
                    })
                    .filter(p => p.y !== null)
                    .slice(-14)
            };

            const hasValidAges = idealSeries.data.some(p =>
                longestCategories.includes(String(p.custom.age_day))
            );
            if (hasValidAges) {
                allSeries.push(idealSeries);
            }

            return {
                props: {
                    legend: {
                        enabled: true,
                        layout: 'horizontal',
                        align: 'center',
                        verticalAlign: 'bottom',
                    },
                    credits: { enabled: false },
                    title: {
                        text: `${this.$t('weeklyWeight')} (${selected.length} ${this.pluralize(selected[0].type, selected.length)})`,
                    },
                    yAxis: {
                        title: { text: `${this.$t('weight')} (g)` },
                    },
                    tooltip: {
                        useHTML: true,
                        formatter: function () {
                            const { week, box } = this.point.custom || {};
                            const semana = week ? `${i18n.t('week')} ${week}` : '-';
                            return `
          <div style="padding: 4px 8px">
            <strong>${semana}${box ? ` (${selected[0].type} ${box})` : ''}</strong><br/>
            ${this.series.name.includes('Ideal')
                                    ? `${i18n.t('idealWeight')}: ${this.y?.toFixed(2)}g`
                                    : `${i18n.t('weeklyWeight')}: ${this.y?.toFixed(2)}g`
                                }
          </div>
        `;
                        },
                    },
                    xAxis: [{
                        title: { text: this.$t('week') },
                        tickInterval: 1,
                        allowDecimals: false,
                        min: Math.min(...longestCategories.map(a => Number(a) / 7)),
                        max: Math.max(...longestCategories.map(a => Number(a) / 7)),
                    }],
                    plotOptions: {
                        series: {
                            marker: { enabled: true },
                        }
                    },
                    series: allSeries,
                }
            };
        },

        histogramGraphic() {
            const divisions = this.getSelectedBatchDivisions?.length
                ? this.getSelectedBatchDivisions
                : this.getSelectedBatch?.batchDivisions || [];

            if (!divisions.length) {
                return { props: { title: { text: this.$t('noData') }, series: [] } };
            }

            const binWidth = 50;

            const allSamples = divisions.flatMap(d => d.samples || []);
            const minValue = Math.min(...allSamples);
            const maxValue = Math.max(...allSamples);

            const start = Math.floor(minValue / binWidth) * binWidth;
            const end = Math.ceil(maxValue / binWidth) * binWidth;

            const categories = [];
            for (let i = start; i < end; i += binWidth) {
                categories.push(`${i}-${i + binWidth}`);
            }

            const colors = ['#00c080', '#0070c0', '#c00000', '#7030a0'];
            const series = divisions.map((division, index) => {
                const bins = categories.map(cat => {
                    const [min, max] = cat.split('-').map(Number);
                    return (division.samples || []).filter(v => v >= min && v < max).length;
                });

                return {
                    name: `${division.type} ${division.identification}`,
                    data: bins,
                    color: colors[index % colors.length],
                    stack: 'boxes'
                };
            });

            return {
                props: {
                    chart: {
                        type: 'column'
                    },
                    title: {
                        text: `${this.$t('histogram')} - ${divisions.map(d => `${d.type} ${d.identification}`).join(', ')}`
                    },
                    credits: { enabled: false },
                    xAxis: {
                        categories,
                        title: { text: `${this.$t('weightRanges')} (g)` },
                        labels: { style: { fontSize: '12px' } }
                    },
                    yAxis: {
                        title: { text: this.$t('totalQty') },
                        stackLabels: { enabled: true }
                    },
                    tooltip: {
                        useHTML: true,
                        shared: true,
                        formatter: function () {
                            return this.points.map(p => `<b>${i18n.t('frequency')} ${p.series.name}</b>: ${p.y}`).join('<br/>');
                        }
                    },
                    plotOptions: {
                        column: {
                            stacking: 'normal',
                            pointPadding: 0.05,
                            groupPadding: 0.05,
                            borderWidth: 0
                        }
                    },
                    series
                }
            };
        },
        sortedSelectedBatchDivisions() {
            if (!this.getSelectedBatchDivisions) return [];
            return [...this.getSelectedBatchDivisions].sort((a, b) => {
                const idA = Number(a.identification) || a.identification;
                const idB = Number(b.identification) || b.identification;
                return idA > idB ? 1 : idA < idB ? -1 : 0;
            })
        },
        grafanaDashboardUrl() {
            const batch = this.getSelectedBatch
            if (!batch) return ''

            const dashboardUid =
                this.getDashboardsSettings?.performance[this.$i18n.locale]

            const baseUrl = process.env.VUE_APP_GRAFANA_URL

            const url = new URL(
                `d/${dashboardUid}`,
                baseUrl
            )

            url.searchParams.set('orgId', '1')
            url.searchParams.set('theme', 'light')
            url.searchParams.set('kiosk', '')

            const now = new Date()

            const openingSeconds =
                differenceInSeconds(now, parseISO(batch.openingDate)) + 60

            const closingSeconds =
                differenceInSeconds(now, parseISO(batch.closingDate)) + 60

            url.searchParams.set(
                'from',
                openingSeconds > 0 ? `now-${openingSeconds}s` : 'now-24h'
            )

            url.searchParams.set(
                'to',
                closingSeconds > 0
                    ? `now-${closingSeconds}s`
                    : `now+${(50 - batch.age) * 24 * 60 * 60}s`
            )

            // variáveis do dashboard
            url.searchParams.set('var-batch_id', batch.id)
            url.searchParams.set('var-sex_id', this.$route.params.sex)

            url.searchParams.set(
                'var-sex_label',
                this.$route.params.sex == 3
                    ? this.$t('male')
                    : this.$t('female')
            )

            url.searchParams.set(
                'var-current_weight',
                batch.weight ? Math.round(batch.weight) : '—'
            )

            url.searchParams.set('var-batch_age', batch.age)
            url.searchParams.set(
                'var-target_weight',
                batch.targetWeight
            )

            return url.toString()
        }
    },
    methods: {
        ...mapActions('environment', ['loadBatchDetails']),
        pluralize(word, count) {
            if (count === 1) return word

            if (word.endsWith('y')) {
                return word.slice(0, -1) + 'ies'
            } else if (word.endsWith('s') || word.endsWith('x') || word.endsWith('z') || word.endsWith('ch') || word.endsWith('sh')) {
                return word + 'es'
            } else {
                return word + 's'
            }
        }
    },
    watch: {
        'getSelectedBatch.id': {
            immediate: true,
            async handler(batchId) {
                if (!batchId) return;
                this.$emit('set-loading', true);

                const module = this.$route.meta.module;

                if (module === 'matrix' || module === 'laying-birds') {
                    await this.loadBatchDetails({ id: batchId, state: 'performance' });
                }
                this.$emit('set-loading', false);
            }
        }
    }
};
</script>
